import React, { useEffect, useRef, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AISimpleGenerationModal } from '../modals/AISimpleGenerationModal';
import { normalizeBackendMediaType } from '../../utils/tagStyles';
import canvaLogo from '../../assets/logos/canva.svg';
import { useToast } from '../../context/ToastContext';
import { CreateImageDropdown } from './media/CreateImageDropdown';
import { AddMoreImagesDropdown } from './media/AddMoreImagesDropdown';
import { UploadDropzone } from './media/UploadDropzone';
import { MediaGrid } from './media/MediaGrid';
import { SocialnessBrowserModal } from './media/SocialnessBrowserModal';
import { CanvaDesignsModal } from './media/CanvaDesignsModal';
import { TagPill } from './media/TagPill';
import { MultiImagesHint } from './media/MultiImagesHint';
import { useImageGenerationState } from '../../hooks/useImageGenerationState';
import { useCanvaIntegration } from '../../hooks/useCanvaIntegration';

interface MediaPostCardProps {
  media: File | null;
  onMediaChange: (file: File | null) => void;
  onClearMedia: () => void;
  existingMediaUrl?: string | null;
  existingMediaType?: 'image' | 'video' | null;
  brandId?: string;
  content?: string;
  selectedPlatforms?: string[];
  onExistingMediaChange?: (url: string | null) => void;
  existingMediaUrls?: string[];
  onExistingMediaUrlsChange?: (urls: string[]) => void;
  currentImageStyle?: string;
  allowMultipleImages?: boolean;
  multipleMedia?: File[];
  onMultipleImagesChange?: (files: File[]) => void;
  postMediaTypeTag?: string | null;
  onCanvaModalToggle?: (open: boolean) => void;
  openCanvaModalOnLoad?: boolean;
  onSaveDraft?: () => Promise<boolean> | void;
}

export const MediaPostCard: React.FC<MediaPostCardProps> = ({
  brandId,
  media,
  onMediaChange,
  onClearMedia,
  existingMediaUrl,
  existingMediaType,
  content = '',
  selectedPlatforms = [],
  onExistingMediaChange,
  existingMediaUrls = [],
  onExistingMediaUrlsChange,
  currentImageStyle,
  allowMultipleImages = false,
  multipleMedia = [],
  onMultipleImagesChange,
  postMediaTypeTag,
  onCanvaModalToggle,
  openCanvaModalOnLoad,
  onSaveDraft,
}) => {
  const { t } = useTranslation();
  useToast();

  // UI state
  const [dragActive, setDragActive] = useState(false);
  const [isCreateImageDropdownOpen, setIsCreateImageDropdownOpen] = useState(false);
  const [isAddMoreImagesDropdownOpen, setIsAddMoreImagesDropdownOpen] = useState(false);
  const addMoreInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const addMoreImagesDropdownRef = useRef<HTMLDivElement>(null);

  // Image generation
  const imageGen = useImageGenerationState({
    brandId,
    content,
    selectedPlatforms,
    onExistingMediaChange,
    onMediaChange,
    setError: (m) => setErrors({ submit: m }),
  });

  // Canva
  const {
    isCanvaLoading,
    isFetchingDesigns,
    isRefreshingDesigns,
    isConfiguringCanva,
    canvaDesigns,
    isCanvaDesignsModalOpen,
    canvaDesignsPage,
    selectedCanvaDesigns,
    isExportingDesigns,
    isSocialnessModalOpen,
    setIsSocialnessModalOpen,
    setIsCanvaDesignsModalOpen,
    setCanvaDesignsPage,
    // configureCanvaIntegration, // not used here
    handleRefreshDesigns,
    handleAddFromCanva,
    handleCanvaDesignToggle,
    exportCanvaDesigns,
    createNewDesign,
    setSelectedCanvaDesigns,
  } = useCanvaIntegration();

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Drag handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!e.dataTransfer.files) return;
    const files = Array.from(e.dataTransfer.files);
    if (allowMultipleImages) {
      const mediaFiles = files.filter((f) =>
        f.type === 'image/jpeg' ||
        f.type === 'image/png' ||
        f.type === 'video/mp4' ||
        f.type === 'video/quicktime'
      );
      if (mediaFiles.length > 0) onMultipleImagesChange?.([...(multipleMedia || []), ...mediaFiles]);
    } else if (files[0]) {
      onMediaChange(files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (allowMultipleImages) {
      const mediaFiles = files.filter((f) =>
        f.type === 'image/jpeg' ||
        f.type === 'image/png' ||
        f.type === 'video/mp4' ||
        f.type === 'video/quicktime'
      );
      if (mediaFiles.length > 0) onMultipleImagesChange?.([...(multipleMedia || []), ...mediaFiles]);
      onMediaChange(null);
    } else if (files[0]) {
      onMediaChange(files[0]);
    }
  };

  const handleRemove = () => {
    onClearMedia?.();
    onExistingMediaChange?.(null);
  };

  const handleRemoveMulti = (index: number) => {
    if (!onMultipleImagesChange) return;
    const next = [...(multipleMedia || [])];
    next.splice(index, 1);
    onMultipleImagesChange(next);
  };

  const handleRemoveExistingUrl = (index: number) => {
    if (!onExistingMediaUrlsChange) return;
    const next = [...(existingMediaUrls || [])];
    next.splice(index, 1);
    onExistingMediaUrlsChange(next);
  };

  // Image generation handlers are provided by hook

  // Canva import opener
  // const navigate = useNavigate(); // not used while opening in new tab
  const [browserUrl] = useState<string | undefined>(undefined);
  const handleCanvaImport = async () => {
    try {
      await onSaveDraft?.();
    } catch {
      // continue even if draft save fails
    }
    const editUrl = await createNewDesign();
    if (editUrl) {
      const w = window.open(editUrl, '_blank', 'noopener,noreferrer');
      if (w) w.opener = null;
      return;
    } else {
      setErrors({ submit: t('errors.canvaCreateFailed', 'Failed to start a new Canva design') });
    }
  };

  const handleCreateImageToggle = () => {
    // Validation: check if content and selected platforms are provided
    const hasContent = content && content.trim() !== '';
    const hasPlatforms = selectedPlatforms && selectedPlatforms.length > 0;

    if (!hasContent && !hasPlatforms) {
      setErrors({ createImage: t('errors.captionAndPlatformRequired', 'Please add a caption and select a publishing channel before creating an image') });
      return;
    }

    if (!hasContent) {
      setErrors({ createImage: t('errors.captionRequired', 'Please add a caption before creating an image') });
      return;
    }

    if (!hasPlatforms) {
      setErrors({ createImage: t('errors.platformRequired', 'Please select a publishing channel before creating an image') });
      return;
    }

    // Clear any existing errors and toggle dropdown
    setErrors({});
    setIsCreateImageDropdownOpen((s) => !s);
  };

  // exportCanvaDesigns provided by hook

  const handleImportSelectedDesigns = async () => {
    if (selectedCanvaDesigns.length === 0) return;
    try {
        const designUrls: string[] = [];
        const designsToExport: string[] = [];

        for (const design of selectedCanvaDesigns) {
        let designUrl: string | null = null;
        const possibleUrls = [design.urls?.thumbnail?.url, design.urls?.view_url, design.urls?.edit_url].filter(Boolean);
          for (const url of possibleUrls) {
          const cleanUrl = (url as string).replace(/^@/, '');
          if (cleanUrl.includes('canva.com/api/design') && (cleanUrl.includes('/view') || cleanUrl.includes('/edit'))) continue;
            if (!cleanUrl.includes('canva.com/api/design')) {
              designUrl = cleanUrl;
              break;
            }
          }
        if (designUrl) designUrls.push(designUrl);
        else designsToExport.push(design.id);
      }

        if (designsToExport.length > 0) {
          try {
            const exportedUrls = await exportCanvaDesigns(designsToExport);
            designUrls.push(...exportedUrls);
        } catch {
          // silently continue with available URLs
          }
        }

      if (designUrls.length > 0) {
        const currentUrls: string[] = [];
        if (existingMediaUrls && existingMediaUrls.length > 0) currentUrls.push(...existingMediaUrls);
        else if (existingMediaUrl) currentUrls.push(existingMediaUrl);
        currentUrls.push(...designUrls);

        if (currentUrls.length > 1) {
          onExistingMediaUrlsChange?.(currentUrls);
          onExistingMediaChange?.(null);
        } else if (currentUrls.length === 1) {
          onExistingMediaChange?.(currentUrls[0]);
          onExistingMediaUrlsChange?.([]);
        }
      } else {
        throw new Error('No valid image URLs found for selected designs.');
      }

      setSelectedCanvaDesigns([]);
      setIsCanvaDesignsModalOpen(false);
    } catch (error: any) {
      setErrors({ submit: error?.message || t('errors.canvaSelectFailed', 'Failed to import selected designs') });
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsCreateImageDropdownOpen(false);
      if (addMoreImagesDropdownRef.current && !addMoreImagesDropdownRef.current.contains(event.target as Node)) setIsAddMoreImagesDropdownOpen(false);
    };
    if (isCreateImageDropdownOpen || isAddMoreImagesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCreateImageDropdownOpen, isAddMoreImagesDropdownOpen]);

  // Detect return from Canva OAuth configuration and auto-open modal
  // Also handle exportUrl from new Canva callback flow
  useEffect(() => {
    const handleOAuthReturn = async () => {
      const isCanvaConfig = sessionStorage.getItem('oauth_canva_config');
      const exportUrl = sessionStorage.getItem('canva_export_url');

      if (isCanvaConfig === 'true') {
        sessionStorage.removeItem('oauth_canva_config');
        setTimeout(async () => {
          try {
            // Pass skipOAuthRedirect=true to prevent infinite OAuth loop:
            // if designs still return 401/403 after OAuth, show error instead of redirecting again
            await handleAddFromCanva(true);
          } catch {}
        }, 1000);
      }

      // Handle new Canva callback flow with exportUrl
      if (exportUrl) {
        sessionStorage.removeItem('canva_export_url');
        try {
          // Add the exportUrl as a new media URL
          const currentUrls = existingMediaUrls?.length ? [...existingMediaUrls] : [];
          if (existingMediaUrl) currentUrls.push(existingMediaUrl);
          currentUrls.push(exportUrl);

          if (currentUrls.length > 1) {
            onExistingMediaUrlsChange?.(currentUrls);
            onExistingMediaChange?.(null);
          } else {
            onExistingMediaChange?.(exportUrl);
            onExistingMediaUrlsChange?.([]);
          }
        } catch (error) {
          console.error('Error handling Canva export URL:', error);
        }
      }
    };
    handleOAuthReturn();
    const handleVisibilityChange = () => {
      if (!document.hidden) handleOAuthReturn();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [existingMediaUrls, existingMediaUrl, onExistingMediaUrlsChange, onExistingMediaChange]);

  // Notify parent when Canva designs modal open state changes
  useEffect(() => {
    onCanvaModalToggle?.(isCanvaDesignsModalOpen);
  }, [isCanvaDesignsModalOpen, onCanvaModalToggle]);

  // Optionally auto-open Canva designs modal when requested by parent (via URL param)
  const didOpenFromQueryRef = useRef(false);
  useEffect(() => {
    if (openCanvaModalOnLoad && !didOpenFromQueryRef.current) {
      didOpenFromQueryRef.current = true;
      // Trigger the same flow as clicking "Add from Canva"
      handleAddFromCanva();
    }
  }, [openCanvaModalOnLoad, handleAddFromCanva]);

  // Clear validation errors when content or platforms are updated
  useEffect(() => {
    if (content && content.trim() !== '' && selectedPlatforms && selectedPlatforms.length > 0) {
      if (errors.createImage) {
        setErrors({});
      }
    }
  }, [content, selectedPlatforms, errors.createImage]);

  // Platform-specific image caps
  const PLATFORM_IMAGE_CAPS: Record<string, number> = { instagram: 20, facebook: 10, linkedin: 9, 'google-business': 10, twitter: 4 };
  const computeSelectedPlatformsCap = (): { minCap: number; caps: Array<{ platform: string; cap: number }> } => {
    const caps = (selectedPlatforms || [])
      .map((p) => ({ platform: p.toLowerCase(), cap: PLATFORM_IMAGE_CAPS[p.toLowerCase()] }))
      .filter((x) => typeof x.cap === 'number');
    if (caps.length === 0) return { minCap: 20, caps: [] };
    const minCap = caps.reduce((min, { cap }) => Math.min(min, cap), caps[0].cap);
    return { minCap, caps };
  };
  const { minCap, caps: selectedCaps } = computeSelectedPlatformsCap();
  const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    linkedin: 'LinkedIn',
    'google-business': 'Google Business',
    twitter: 'Twitter',
  };
  const strictest = selectedCaps.length > 0 ? selectedCaps.reduce((min, curr) => (curr.cap < min.cap ? curr : min), selectedCaps[0]) : undefined;
  const strictestPlatformName = strictest ? PLATFORM_DISPLAY_NAMES[strictest.platform] || strictest.platform : t('common.platform', 'Platform');

  const totalImagesCount = (media ? 1 : 0) + (existingMediaUrl ? 1 : 0) + (existingMediaUrls?.length || 0) + (multipleMedia?.length || 0);

  const shouldShowGenerateButton = (() => {
    if (!brandId) return false;
    const normalized = postMediaTypeTag ? normalizeBackendMediaType(postMediaTypeTag || undefined) : undefined;
    if (normalized && (normalized === 'document' || normalized === 'multi' || normalized === 'video')) return false;
    if (existingMediaType === 'video') return false;
    if (media && typeof media.type === 'string' && media.type.startsWith('video/')) return false;
    if ((existingMediaUrls?.length || 0) > 1) return false;
    if ((multipleMedia?.length || 0) > 1) return false;
    return true;
  })();

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-black">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">{t('mediaPost.title', 'Media')}</h2>
          <TagPill tag={postMediaTypeTag} />
        </div>
        <div className="flex items-center gap-3" ref={dropdownRef}>
          {shouldShowGenerateButton && (
            <CreateImageDropdown
              isOpen={isCreateImageDropdownOpen}
              isGenerating={imageGen.isLoading}
              isCanvaLoading={isCanvaLoading}
              onToggle={handleCreateImageToggle}
              onGenerate={() => {
                setIsCreateImageDropdownOpen(false);
                imageGen.setIsOpen(true);
              }}
              onCanva={() => {
                setIsCreateImageDropdownOpen(false);
                handleCanvaImport();
              }}
              canvaLogo={canvaLogo}
            />
          )}
                </div>
              </div>

        {errors.createImage && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-md p-2">
            <p className="text-sm text-red-600">{errors.createImage}</p>
          </div>
        )}

        <div className="mb-6">
          <div className="mb-2">
            <div className="flex items-end gap-2">
              <h3 className="text-sm font-medium text-gray-700">{t('postContent.media', 'Media')}</h3>
              {allowMultipleImages && (
              <span className="text-[11px] text-gray-600 italic">{totalImagesCount}/{minCap} {t('common.images', 'images')}</span>
              )}
            </div>
            {allowMultipleImages && (
              <span className="block text-[10px] text-gray-500 italic mt-0.5">
                {t('hints.platformCapNamed', '{{platform}} allows multi-images up to {{cap}} images', { platform: strictestPlatformName, cap: minCap })}
              </span>
            )}
          </div>

          {multipleMedia.length > 0 || existingMediaUrls.length > 0 || media || existingMediaUrl ? (
            <div className="relative">
            <MediaGrid
              existingMediaUrls={existingMediaUrls}
              multipleMedia={multipleMedia}
              singleMedia={media}
              existingMediaUrl={existingMediaUrl}
              existingMediaType={existingMediaType || null}
              onRemoveSingle={handleRemove}
              onRemoveMulti={handleRemoveMulti}
              onRemoveExistingUrl={onExistingMediaUrlsChange ? handleRemoveExistingUrl : undefined}
            />

              <div className="mt-3 flex flex-col items-center justify-center gap-1">
              <MultiImagesHint count={existingMediaUrls.length + multipleMedia.length} minCap={minCap} selectedCaps={selectedCaps} />

              <input ref={addMoreInputRef} type="file" accept=".jpg,.jpeg,.png,.mp4,.mov" multiple className="hidden" onChange={handleChange} />
              <div ref={addMoreImagesDropdownRef} className="relative">
                <AddMoreImagesDropdown
                  isOpen={isAddMoreImagesDropdownOpen}
                  onToggle={() => setIsAddMoreImagesDropdownOpen((s) => !s)}
                  onAddFromDesktop={() => addMoreInputRef.current?.click()}
                  onAddFromCanva={handleAddFromCanva}
                  isBusy={isFetchingDesigns || isConfiguringCanva}
                  canvaLogo={canvaLogo}
                />
                      </div>
                    </div>
                </div>
        ) : (
          <>
            <UploadDropzone
              dragActive={dragActive}
              allowMultipleImages={allowMultipleImages}
              onDrag={handleDrag}
              onDrop={handleDrop}
                onChange={handleChange}
            />

            <div className="mt-4 flex justify-center">
              <button
                onClick={handleAddFromCanva}
                disabled={isFetchingDesigns || isConfiguringCanva}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-white border border-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isFetchingDesigns || isConfiguringCanva ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-t-transparent border-primary-600 rounded-full animate-spin"></div>
                    {isConfiguringCanva ? t('integrations.configuring', 'Configuring...') : t('common.loading', 'Loading...')}
                  </>
                ) : (
                  <>
                    <img src={canvaLogo} alt="Canva" className="w-4 h-4 mr-2" />
                    {t('actions.addFromCanva', 'Add from Canva')}
                  </>
                )}
              </button>
            </div>
          </>
          )}
        </div>

        {errors.submit && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        <AISimpleGenerationModal
        isOpen={imageGen.isOpen}
          onClose={() => {
          imageGen.setIsOpen(false);
          imageGen.setTemporaryUrl(null);
        }}
        onGenerate={imageGen.handleGenerate}
        onValidate={imageGen.handleValidate}
        isLoading={imageGen.isLoading}
          currentImageStyle={currentImageStyle || 'auto'}
          currentImageSize="square"
        generatedImageUrl={imageGen.temporaryUrl}
      />

      <SocialnessBrowserModal isOpen={isSocialnessModalOpen} onClose={() => setIsSocialnessModalOpen(false)} url={browserUrl} />

      <CanvaDesignsModal
        isOpen={isCanvaDesignsModalOpen}
        isExporting={isExportingDesigns}
        isRefreshing={isRefreshingDesigns}
        canvaDesigns={canvaDesigns}
        canvaDesignsPage={canvaDesignsPage}
        selectedCanvaDesigns={selectedCanvaDesigns}
        onClose={() => {
                  if (!isExportingDesigns) {
                    setIsCanvaDesignsModalOpen(false);
                    setSelectedCanvaDesigns([]);
                  }
                }}
        onToggleSelect={handleCanvaDesignToggle}
        onImportSelected={handleImportSelectedDesigns}
        onRefresh={handleRefreshDesigns}
        setPage={setCanvaDesignsPage}
        canvaLogo={canvaLogo}
      />
                            </div>
                          );
};


